import {Component, effect, EventEmitter, inject, Input, Output, ViewEncapsulation} from '@angular/core';
import {TableControlComponent} from '../table-control/table-control.component';
import {IControlObject} from '../../../../../../nucleus/src/lib/interfaces/control-object.interface';
import {EControlActions} from '../../../../../../nucleus/src/lib/control/base-control/control-actions.enum';
import {mergeMap, tap} from 'rxjs/operators';
import {IApiRow} from '../table-control/api-row.interface';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AbstractControl} from '@angular/forms';
import {
  IOptionChange,
  SelectElementComponent,
} from '../form-control/dynamic-form/form-element/select-element/select-element.component';
import {ISelectFormElement, ISelectFormOption} from '../form-control/dynamic-form/form-element/form-data.interface';
import {IApiTableData} from '../table-control/interfaces/api-table-data.interface';
import {MatProgressBar} from '@angular/material/progress-bar';
import {patchState} from '@ngrx/signals';
import {Observable} from 'rxjs';

export const tableRowSelectionImports = [SelectElementComponent, MatProgressBar];

@Component({
  selector: 'kpi4me-table-row-selection',
  templateUrl: './table-row-selection.component.html',
  styleUrls: ['./table-row-selection.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: tableRowSelectionImports,
})
export class TableRowSelectionComponent extends TableControlComponent {
  private _snackBar: MatSnackBar = inject(MatSnackBar);

  @Input() override toModel(d: IApiTableData): void {}
  @Input() showNew: boolean = false;
  @Input() newButtonLabel: string = 'Neu';
  @Input() showDelete: boolean = true;
  @Input() disabled: boolean = false;
  @Input() singleStringArray: boolean = true;
  @Input() getDisplayValue: (row: any) => string;
  @Input() multiple: boolean = false;
  @Input() editEnabled: boolean = true;
  @Input() formControlElement: AbstractControl;
  @Input() selectionData: ISelectFormElement;
  @Output() selectedRowChange: EventEmitter<IApiRow> = new EventEmitter<IApiRow>();
  @Output() selectionDataChanged: EventEmitter<IControlObject> = new EventEmitter<IControlObject>();
  @Output() newClicked: EventEmitter<any> = new EventEmitter();
  @Output() rowsChanged: EventEmitter<IApiRow[]> = new EventEmitter<IApiRow[]>();

  rows: IApiRow[];

  compareBy: (e1: any, e2: any) => boolean = (e1: any, e2: any): boolean => {
    return e1 && e2 && e1.objid === e2.objid;
  };

  constructor() {
    super();
    effect((): void => {
      const apiData: IApiTableData = this.apiData();

      if (apiData) {
        this.fetchRows().subscribe();
      }
    }, {allowSignalWrites: true});
  }

  fetchRows(): Observable<object[]> {
    patchState(this.state, {loading: true});
    return this._tableService.tablePostFetchRows({
      _parameters: [
        this.instanceId(),
        0,
        this.apiData().rowcount,
        0,
        this.apiData().displayedColumns.length - 1
      ]
    }).pipe(tap((_: object []): void => {
      const rows = _ as IApiRow[];
      this.setRows(rows)
      patchState(this.state, {loading: false});
    }));
  }

  protected setRows(rows: IApiRow[]): void {
    console.log(rows);
    this.rows = rows;
    this.rowsChanged.emit(rows);
    this.selectionData = {
      ...this.selectionData,
      options: rows.map((r: IApiRow): ISelectFormOption => {
        return {
          name: this.getDisplayValue ? this.getDisplayValue(r) : JSON.stringify(r),
          value: r,
        }
      }),
      multiple: this.multiple,
    }
    this.cdr.detectChanges();
  }

  onSelectedRowChange(event: any): void {
    if (!this.isControlObject(event)) {
      this.selectedRowChange.emit(event);
    } else {
      this.selectionDataChanged.emit(event);
    }
  }

  isControlObject(evt: any): boolean {
    if (evt == null) return true;
    return (
      typeof evt === 'object' &&
      typeof evt?.type === 'string' &&
      typeof evt?.signature === 'string' &&
      typeof evt?.rowid === 'number'
    );
  }

  @Input() onNew(evt: string): void {
    this._tableControlService.tableControlNewLine({InstanceId: this.instanceId()})
      .pipe(
        tap((): void => {
          this.changeAndFetchInstance();
        })
      ).subscribe(
      (_: object[]): void => {
        this.controlStore.controlDataChanged$.emit({GUID: this.GUID, changeType: EControlActions.create})
        this.newClicked.emit();
      }
    );
  }

  @Input() onEdit(evt: IOptionChange): void {
    throw new Error('Edit is not implemented for this component. ' +
      'Please override and implement the method onEdit(evt: string): void');
  }

  // deletes the row by calling the api. Re-fetches the table afterward
  delete(obj: ISelectFormOption): void {
    this._tableControlService.tableControlDeleteLine(
      {InstanceId: this.instanceId(), RowObjects: [obj.value.obj]}
    ).pipe(
      tap((result: boolean): void => {
        if (result == false) {
          this._snackBar.open('Der Eintrag kann nicht gelöscht werden', 'ok', {duration: 5000})
        } else {
          this.rowDeleted.emit();
          if (this.formControlElement.value == null || this.formControlElement.value.signature === obj.signature) {
            this.formControlElement.reset();
            this.onSelectedRowChange(null)
          }
          this.controlStore.controlDataChanged$.emit({GUID: this.GUID, changeType: EControlActions.delete})
        }
      }),
      mergeMap((): Observable<object[]> => {
        return this.fetchRows()
      })
    ).subscribe();
  }
}
